export type CigarStatus = "yes" | "no" | "unsure";
export type Liquor = "yes" | "no" | "byo" | "unknown";

export interface Cuisine {
  key: string;
  label_zh: string;
  venue_count: number;
}

/** 備註類別（見 backend/tags.py）。封閉清單 —— 前端唔可以自己作。 */
export interface Tag {
  key: string;
  label_zh: string;
  group: string;
  venue_count: number;
}

export interface VenueListItem {
  id: number;
  name: string;
  name_en: string | null;
  district: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  phone: string | null;
  premises_type: string | null;
  cuisines: string[];
  distance_m: number | null;

  /** 官方露天座位許可時間 —— 食雪茄真正受呢個限制 */
  osa_hours_text: string | null;
  /** 上述嘅中文版；譯唔到就 null，前端跌返原文 */
  osa_hours_zh: string | null;
  /** OpenRice 屬性標籤「寵物友善餐廳」（餐廳自己申報，唔係官方數據） */
  pet_friendly: boolean;
  osa_start: string | null;
  osa_end: string | null;
  osa_end_min: number | null;
  /** true = 原文唔同日唔同時間，單一範圍只係外框，要顯示原文 */
  osa_multi: boolean;
  /** 店舖自己幾點閂 —— 同上面成日唔同 */
  shop_closes: string | null;
  open_now: boolean | null;

  cigar_status: CigarStatus;
  outdoor_detail: string | null;
  last_visited: string | null;
  confidence: string | null;
  licence_count: number;
  is_open_air: boolean;
}

export interface Visit {
  id: number;
  visited_on: string;
  cigar_confirmed: CigarStatus;
  area_type: string | null;
  who: string;
  remark: string | null;
  /** 備註類別 key（見 types.ts Tag / backend/tags.py） */
  tags: string[];
  photos: { id: number; path: string; caption: string | null }[];
}

export interface VenueDetail extends VenueListItem {
  opening_hours_text: string | null;
  opening_hours_zh: string | null;
  opening_hours: Record<string, string[][]>;
  liquor: Liquor;
  outdoor_space: string;
  cigar_area_type: string | null;
  shop: string | null;
  visit_count: number;
  first_visited: string | null;
  status: string;
  data_source: string | null;
  licences: {
    licence_no: string;
    licence_type: string | null;
    licence_expiry: string | null;
    confidence: string | null;
  }[];
  visits: Visit[];
}

export type Coords = { lat: number; lng: number } | null;

/**
 * 「我到訪記錄」篩選。全部都會落喺**同一次**到訪上面（後端用一個 EXISTS
 * 子查詢），所以「食得 + 蚊多」唔會中一間「一次食得、另一次蚊多」嘅餐廳。
 */
export type VisitFilters = {
  tag?: string;
  verdict?: string;
  area?: string;
  who?: string;
  visited?: "" | "yes" | "no";
};

export function hasVisitFilters(f: VisitFilters): boolean {
  return Boolean(f.tag || f.verdict || f.area || f.who || f.visited);
}

/**
 * 列表畫面嘅模式。擺喺 types.ts 而唔係 App.tsx：List.tsx 要用呢個型別，
 * 如果由 App 度 export 就會變成 App → List → App 循環 import。
 */
export type ListMode =
  | { kind: "nearby" }
  | { kind: "cuisine"; key: string; label?: string; district?: string; filters?: VisitFilters;
      /** 篩選條件嘅人話描述，由 Home 砌好（佢有標籤清單可以譯 key） */ filterLabel?: string }
  | { kind: "visit"; filters: VisitFilters; filterLabel?: string };

/* -------------------------------------------------------------- 定位狀態
 * 分開「拒絕」同「攞唔到 fix」：對用家嚟講係完全唔同嘅兩件事 —— 一個要入
 * 瀏覽器設定，另一個再試一次就得。舊版一律當成「拒絕」再靜靜轉排序，睇落
 * 就好似個 app 唔識搵附近餐廳。
 */
/** 一個區份同佢嘅中心點，用嚟做手動定位後備，同時做地區下拉。 */
export type District = {
  district: string;
  lat: number;
  lng: number;
  venue_count: number;
  /** 香港島／九龍／新界；對唔到係 null */
  region: string | null;
};

export type GeoState =
  | "asking"
  | "ok"
  | "denied"
  | "unavailable"
  | "timeout"
  | "insecure"
  /* 開畫面時自動問（冇用戶手勢）被拒。Safari 要求定位請求由手勢觸發，
     所以呢個「拒絕」未必係真拒絕 —— 要用戶撳一下先分得出。唔可以就噉
     當成「你拒絕咗」，否則會叫用戶去改一個根本冇問題嘅設定。 */
  | "needGesture";

export function geoMessage(s: GeoState): string | null {
  switch (s) {
    case "denied":
      return "位置權限被拒絕 —— 要喺瀏覽器設定入面開返";
    case "needGesture":
      return "撳下面個掣，用你而家嘅位置搵附近餐廳";
    case "timeout":
      return "定位逾時（室內或訊號弱好常見）—— 再試一次通常就得";
    case "unavailable":
      return "攞唔到位置 —— 部機嘅定位可能熄咗";
    case "insecure":
      return "要用 https 先攞得到位置";
    default:
      return null;
  }
}
